import { FileManagerEndpoints } from '@/api/endpoints/FileManagerEndpoints';
import { destroy, get, post, put } from '@/api/axios';
import { type AttachmentDto } from '@/app/models/AttachmentDto';
import { type SystemStorageDetailsDto } from '@/app/models/SystemStorageDetailsDto';
import { type UploadExternalUrlRequestDto } from '@/app/pages/file-manager/core/_models';
import { uploadAttachment } from '@/app/modules/attachments/core/_requests';
import { type AttachmentUploadProgress } from '@/app/modules/attachments/core/_models';

export const uploadFile = (
  image: File,
  onUploadProgress: AttachmentUploadProgress,
  temporary: boolean = true,
  local: boolean = false,
) => {
  return uploadAttachment(image, { endpoint: FileManagerEndpoints.uploadFile, temporary, local, onUploadProgress });
};

export const uploadUrl = async (
  payload: UploadExternalUrlRequestDto,
): Promise<{ fileName: string; fileSize: number; data: AttachmentDto }> => {
  try {
    const response = await post<AttachmentDto>(FileManagerEndpoints.uploadUrl, payload);
    if (response.status === 200) {
      return {
        fileName: response.data.name ?? '',
        fileSize: response.data.size ?? 0,
        data: response.data,
      };
    }
    throw new Error(`Unexpected response status: ${response.status}`);
  } catch (error) {
    console.error('Error uploading URL:', error);
    throw error;
  }
};

export const uploadBase64 = (
  base64String: string,
  fileName: string,
  onUploadProgress: AttachmentUploadProgress,
  temporary: boolean = true,
  local: boolean = false,
) => {
  // Remove o prefixo data:image/...;base64, se existir
  const base64Data = base64String.replace(/^data:image\/\w+;base64,/, '');

  // Converte Base64 para Blob
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  // Detecta o tipo MIME da imagem
  const mimeType = base64String.match(/^data:([^;]+);base64,/)?.[1] || 'image/png';
  const blob = new Blob([byteArray], { type: mimeType });

  // Converte Blob para File
  const file = new File([blob], fileName, { type: mimeType });

  // Reutiliza a função uploadFile existente
  return uploadFile(file, onUploadProgress, temporary, local);
};

export const list = (name: string = '') => {
  return get(FileManagerEndpoints.list, {
    params: { search: name },
  });
};

export const update = (id: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return put(FileManagerEndpoints.edit(id), formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const find = (id: string) => {
  return get(FileManagerEndpoints.find(id));
};

export const remove = (id: string) => {
  return destroy(FileManagerEndpoints.delete(id));
};

export const getSystemStorageDetails = () => {
  return get<SystemStorageDetailsDto>(FileManagerEndpoints.systemStorageDetails);
};
